import React from "react";
import { EntityLink } from "@/components/shared/EntityLink";
import { EntityAuditFooter } from "@/components/shared/EntityAuditFooter";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Printer,
  Layers,
  Cpu,
  CheckCircle2,
  Clock,
  ExternalLink,
  Maximize2,
  Palette,
  ShieldCheck,
  AlertTriangle
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface CTPPlateDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: any;
  onStatusChange?: (layoutId: string, status: any) => void;
}

const COLOR_PLATES = [
  { code: "C", name: "Cyan (Xanh)", angle: "15°", lpi: 175, bg: "bg-cyan-500 text-white", border: "border-cyan-200" },
  { code: "M", name: "Magenta (Đỏ sen)", angle: "75°", lpi: 175, bg: "bg-pink-500 text-white", border: "border-pink-200" },
  { code: "Y", name: "Yellow (Vàng)", angle: "0° / 90°", lpi: 175, bg: "bg-yellow-400 text-yellow-950", border: "border-yellow-200" },
  { code: "K", name: "Black (Đen)", angle: "45°", lpi: 175, bg: "bg-slate-900 text-white", border: "border-slate-300" },
];

export default function CTPPlateDetailModal({
  open,
  onOpenChange,
  layout,
  onStatusChange,
}: CTPPlateDetailModalProps) {
  const navigate = useNavigate();

  if (!layout) return null;

  const plateCount = layout.plateCount || 4;
  const plateList = COLOR_PLATES.slice(0, Math.min(plateCount, 4));

  // Nếu nhiều hơn 4 kẽm, thêm lá kẽm pha Pantone
  if (plateCount > 4) {
    for (let i = 5; i <= plateCount; i++) {
      plateList.push({
        code: `P${i - 4}`,
        name: `Màu pha Pantone #${i - 4}`,
        angle: "45°",
        lpi: 175,
        bg: "bg-purple-600 text-white",
        border: "border-purple-200",
      });
    }
  }

  const handleNavigateToLayout = () => {
    onOpenChange(false);
    navigate({
      to: "/production/print-layouts/$layoutId",
      params: { layoutId: layout.id },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-3 border-b border-border bg-slate-50">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-base text-indigo-600">
                {layout.id ? <EntityLink entityType="layout" id={layout.id} code={layout.layoutCode || "BẢN BÌNH CTP"} /> : layout.layoutCode || "BẢN BÌNH CTP"}
              </span>
              <Badge variant="outline" className="text-xs font-mono">
                {layout.version || "v1"}
              </Badge>
              <Badge variant="outline" className="text-xs font-semibold bg-white">
                {layout.status === "COMPLETED" ? "Đã xuất kẽm" : layout.status === "PLATE_OUTPUT" ? "Đang ghi kẽm" : "Chờ xử lý"}
              </Badge>
            </div>
          </div>
          <DialogTitle className="text-sm font-semibold text-slate-800 mt-1">
            {layout.name || "Bản bình in offset"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Hồ sơ bộ bản kẽm CTP, thông số góc tram LPI và thiết bị ghi quang học
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Thông số kỹ thuật kẽm */}
          {/* Góc tram / LPI / công nghệ kẽm cố định và khổ mặc định đã bỏ (02/11/2026): không phải dữ liệu bài bình */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2.5 bg-slate-50 rounded-lg border border-border">
              <div className="text-[11px] text-slate-500">Tổng Số Lá Kẽm</div>
              <div className="font-mono font-bold text-sm text-indigo-600 mt-0.5">{plateCount} lá</div>
            </div>
            <div className="p-2.5 bg-slate-50 rounded-lg border border-border">
              <div className="text-[11px] text-slate-500">Khổ Bản Kẽm Ghi</div>
              <div className="font-mono font-bold text-xs text-slate-800 mt-0.5">{layout.sheetSize || "—"}</div>
            </div>
          </div>

          {/* Bảng Danh Sách Các Lá Màu Thành Phần */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Palette className="w-3.5 h-3.5 text-indigo-600" />
                Danh Sách Từng Lá Kẽm Thành Phần
              </span>
              <span className="text-[11px] text-slate-400">Bộ kẽm nguyên vẹn 100%</span>
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-xs font-semibold">
                    <TableHead className="w-[80px]">Màu</TableHead>
                    <TableHead>Tên Lá Kẽm</TableHead>
                    <TableHead className="w-[100px] text-center">Góc Tram</TableHead>
                    <TableHead className="w-[90px] text-center">Độ Phân Giải</TableHead>
                    <TableHead className="w-[110px] text-right">Trạng Thái</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plateList.map((plate, idx) => (
                    <TableRow key={idx} className="text-xs">
                      <TableCell>
                        <span className={`inline-flex w-6 h-6 rounded-md items-center justify-center font-bold text-xs ${plate.bg}`}>
                          {plate.code}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium text-slate-800">
                        {plate.name}
                      </TableCell>
                      <TableCell className="text-center font-mono font-semibold text-slate-700">
                        {plate.angle}
                      </TableCell>
                      <TableCell className="text-center font-mono text-slate-600">
                        {plate.lpi} LPI
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            layout.status === "COMPLETED"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : layout.status === "PLATE_OUTPUT"
                              ? "bg-blue-50 text-blue-700 border-blue-300"
                              : "bg-slate-50 text-slate-600"
                          }`}
                        >
                          {layout.status === "COMPLETED" ? "Đã ghi" : layout.status === "PLATE_OUTPUT" ? "Đang ghi" : "Chờ ghi"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Cảnh Báo An Toàn Thợ Máy CTP */}
          <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-lg flex items-start gap-2.5 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">Lưu ý kiểm tra KCS trước khi giao xưởng in:</div>
              <div className="text-[11px] text-amber-700 mt-0.5">
                Kiểm tra điểm tram 2% - 98%, vạch canh bông chữ thập chéo góc, lề nhíp kéo giấy và đối chiếu đúng mã bài bình với Lệnh Sản Xuất F0.
              </div>
            </div>
          </div>
        </div>

        {layout.id && (
          <div className="px-4 pb-2">
            <EntityAuditFooter entityType="print_layout" entityId={layout.id} fallback={{ created: layout.createdAt, updated: layout.updatedAt }} />
          </div>
        )}

        {/* Footer Buttons */}
        <div className="p-4 border-t border-border bg-slate-50 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={handleNavigateToLayout}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Mở bài bình
          </Button>

          <div className="flex items-center gap-2">
            {layout.status === "PENDING" && onStatusChange && (
              <Button
                size="sm"
                className="text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => {
                  onStatusChange(layout.id, "PLATE_OUTPUT");
                  onOpenChange(false);
                }}
              >
                <Printer className="w-3.5 h-3.5" /> Bắt Đầu Ghi Kẽm
              </Button>
            )}

            {layout.status === "PLATE_OUTPUT" && onStatusChange && (
              <Button
                size="sm"
                className="text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => {
                  onStatusChange(layout.id, "COMPLETED");
                  onOpenChange(false);
                }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Xác Nhận Đã Xuất Kẽm
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => onOpenChange(false)}
            >
              Đóng
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
